import {Injectable} from "@angular/core";
import {OBJLoader} from "three/examples/jsm/loaders/OBJLoader";
import {GLTFLoader} from "three/examples/jsm/loaders/GLTFLoader";
import {EquirectangularReflectionMapping, Group, Mesh, MeshStandardMaterial, Object3D, TextureLoader} from "three";
import {Texture} from "three/src/textures/Texture";
import {STLLoader} from "three/examples/jsm/loaders/STLLoader";
import {FBXLoader} from "three/examples/jsm/loaders/FBXLoader";
import {RGBELoader} from "three/examples/jsm/loaders/RGBELoader";

/**
 * Resurssin tyypin tunnisteet
 */
export type ResourceType = "texture" | "obj" | "stl" | "fbx" | "gltf" | "hdr";

/**
 * Tieto yhdestä ladatusta resurssista.
 */
interface ResourceCacheItem {
    resource: Object3D | Texture | null;
    usedBy: Set<string>;         // Kuka/mikä käyttää tätä resurssia
    isRemovable: boolean;        // Jos true, resurssi voidaan vapauttaa kun käytöstä vapautuu
    resourceType: ResourceType;  // Tunniste, jotta osaamme tuhoamisen
    promise: Promise<Object3D | Texture> | null; // Latausprosessi, jos kesken
}

/**
 * ResourceManager huolehtii 3D-mallien ja tekstuurien
 * lataamisesta, välimuistittamisesta sekä vapauttamisesta.
 */
@Injectable({providedIn: 'root'})
export class ResourceManagerService {
    /**
     * Sisäinen välimuisti, johon tallennetaan ladatut resurssit.
     * - Avain: Tiedostopolku (esim. "assets/models/obj1.obj")
     * - Arvo: ResourceCacheItem
     */
    private readonly cache: Map<string, ResourceCacheItem> = new Map();

    // Yksittäiset loaderit eri tiedostotyypeille
    private textureLoader = new TextureLoader();
    private objLoader = new OBJLoader();
    private stlLoader = new STLLoader();
    private fbxLoader = new FBXLoader();
    private gltfLoader = new GLTFLoader();
    private hdrLoader = new RGBELoader();

    /**
     * Lataa (tai hakee välimuistista) halutun resurssin.
     * @param path Polku resurssiin
     * @param type Minkä tyyppinen resurssi (texture, obj, stl, fbx, gltf)
     * @param ownerId Kuka/mikä käyttää resurssia (esim. luokan nimi tai uuid)
     * @param isRemovable Jos true, resurssi voidaan vapauttaa muistista kun kukaan ei enää käytä
     */
    public async loadResource(
        path: string,
        type: ResourceType,
        ownerId: string,
        isRemovable: boolean = true
    ): Promise<Object3D | Texture> {
        // Onko jo välimuistissa?
        const cached = this.cache.get(path);
        if (cached) {
            // Päivitetään usedBy ja isRemovable
            cached.usedBy.add(ownerId);

            if (!isRemovable) { cached.isRemovable = false; }

            // Jos resurssi on jo ladattu, palauta se suoraan
            if(cached.resource !== null){

                if (cached.resource instanceof Texture) { return cached.resource; } //return Texture directly

                return (cached.resource as Object3D).clone(true); //return clone of Object3D

            } else{ //cached items promise hasn't returned resource, link the promise to set the resource for another invoker also when it's loaded

                return new Promise<Object3D|Texture>((resolve, reject) => {
                    try {

                        cached.promise.then( resource => { //latauksen suoritettua

                            if (resource instanceof Texture) {
                                resolve(resource); // Tekstuurin voi käyttää suoraan

                            } else {
                                resolve(resource.clone(true)); //Object3D
                            }
                        });

                    } catch (e) {
                        reject(e);

                    }
                });
            }
        }

        // Ei ole välimuistissa, ladataan
        const promise = this.loadByType(path, type);

        // Lisätään välimuistiin
        const newItem: ResourceCacheItem = {
            resource: null,
            usedBy: new Set([ownerId]),
            isRemovable,
            resourceType: type,
            promise
        };

        this.cache.set(path, newItem);

        return new Promise<Object3D|Texture>((resolve, reject) => {
            try {
                promise.then( resource => { //latauksen suoritettua

                    newItem.resource = resource; // Tallenna ladattu resurssi cacheen

                    if (resource instanceof Texture) {
                        resolve(resource);
                    } else {
                        resolve(resource.clone(true)); //Object3D
                    }

                });
            } catch (e) {
                reject(e);
            }
        });


    }

    /**
     * Owner ilmoittaa, ettei se enää käytä tiettyä resurssia.
     * Jos kukaan ei käytä resurssia ja isRemovable = true,
     * resurssi vapautetaan muistista.
     *
     * @param path Resurssin tiedostopolku
     * @param ownerId Tunniste, joka haluaa vapauttaa resurssin
     */
    public releaseResource(path: string, ownerId: string): void {
        const cached = this.cache.get(path);
        if (!cached) {
            // Resurssia ei ole ladattu tai se on jo vapautettu
            console.warn(`No resource found in cache for path: ${path}`);
            return;
        }

        // Poista omistaja listalta
        cached.usedBy.delete(ownerId);

        // Jos kukaan ei enää käytä resurssia...
        if (cached.usedBy.size === 0) {
            // ... ja resurssi on merkitty poistettavaksi,
            // vapautetaan se muistista
            if (cached.isRemovable) {
                this.disposeResource(cached.resource, cached.resourceType);
                this.cache.delete(path);
                console.log(`Resource freed: ${path}`);
            } else {
                // Resurssi jätetään välimuistiin (pysyvä resurssi).
                console.log(`Resource at ${path} not removed (isRemovable = false).`);
            }
        }
    }

    /**
     * Esilataa joukon resursseja rinnakkain. Palauttaa promisena,
     * kun kaikki ovat ladattu.
     */
    public async prefetchResources(
        resources: Array<{
            path: string;
            type: ResourceType;
            isRemovable?: boolean;
        }>,
        ownerId: string
    ): Promise<void> {
        const tasks = resources.map((res) =>
            this.loadResource(
                res.path,
                res.type,
                ownerId,
                res.isRemovable !== false // jos ei annettu, oletus true
            )
        );
        await Promise.all(tasks);
    }

    /**
     * Vapauttaa kaikki välimuistissa olevat resurssit, joiden isRemovable = true
     * (eli kukaan ei käytä niitä tai ne käyttävät on jo vapautettu).
     *
     * Huomaa, että jos resurssia edelleen käyttää joku, se ei poistu.
     */
    public clearRemovable(): void {
        for (const [path, item] of this.cache.entries()) {
            if (item.isRemovable && item.usedBy.size === 0) {
                this.disposeResource(item.resource, item.resourceType);
                this.cache.delete(path);
                console.log(`Resource forcibly freed (clearRemovable): ${path}`);
            }
        }
    }

    /**
     * Tyhjentää koko välimuistin (myös isRemovable = false).
     * Kaikki resurssit vapautetaan. Käytä harkiten!
     */
    public clearAll(): void {
        for (const [path, item] of this.cache.entries()) {
            this.disposeResource(item.resource, item.resourceType);
            this.cache.delete(path);
        }
        console.log(`All resources have been cleared from cache.`);
    }

    /**
     * Varsinainen latauslogiikka tiedostotyypeittäin.
     * Palauttaa aina Promise<Object3D | Texture>.
     */
    private loadByType(path: string, type: ResourceType): Promise<Object3D | Texture> {
        switch (type) {
            case "texture":
                return this.loadTexture(path);
            case "obj":
                return this.loadOBJ(path);
            case "stl":
                return this.loadSTL(path);
            case "fbx":
                return this.loadFBX(path);
            case "gltf":
                return this.loadGLTF(path);
            case "hdr":
                return this.loadHDR(path);
            default:
                return Promise.reject(`Unsupported resource type: ${type}`);
        }
    }

    // Latausfunktiot eri 3D-formaateille

    private loadTexture(path: string): Promise<Texture> {
        return new Promise((resolve, reject) => {
            this.textureLoader.load(
                path,
                (texture) => resolve(texture),
                undefined,
                (err) => reject(err)
            );
        });
    }

    private loadOBJ(path: string): Promise<Object3D> {
        return new Promise((resolve, reject) => {
            this.objLoader.load(
                path,
                (obj) => resolve(obj),
                undefined,
                (err) => reject(err)
            );
        });
    }

    private loadSTL(path: string): Promise<Object3D> {
        return new Promise((resolve, reject) => {
            this.stlLoader.load(
                path,
                (geometry) => {
                    // Luo Mesh geometrysta ja yksinkertaisesta materiaalista
                    const material = new MeshStandardMaterial();
                    const mesh = new Mesh(geometry, material);
                    mesh.name = "STLModel";
                    resolve(mesh);
                },
                undefined,
                (err) => reject(err)
            );
        });
    }

    private loadFBX(path: string): Promise<Object3D> {
        return new Promise((resolve, reject) => {
            this.fbxLoader.load(
                path,
                (obj) => resolve(obj),
                undefined,
                (err) => reject(err)
            );
        });
    }

    private loadGLTF(path: string): Promise<Object3D> {
        return new Promise((resolve, reject) => {
            this.gltfLoader.load(
                path,
                (gltf) => {
                    // Palautetaan gltf.scene
                    resolve(gltf.scene);
                },
                undefined,
                (err) => reject(err)
            );
        });
    }

    private loadHDR(path: string): Promise<Texture> {
        return new Promise((resolve, reject) => {
            new RGBELoader().load(
                path,
                (texture) => {
                    // Usein HDR-textuureissa halutaan tämä mapping
                    texture.mapping = EquirectangularReflectionMapping;
                    resolve(texture);
                },
                undefined,
                (err) => reject(err)
            );
        });
    }

    /**
     * Tuhoaa annetun 3D-objektin tai tekstuurin vapauttaen sen resurssit.
     */
    private disposeResource(
        resource: Object3D | Texture | null,
        type: ResourceType
    ): void {
        if (!resource) return;

        switch (type) {
            case "texture":
            case "hdr":
                // Tekstuurit — suora dispose
                (resource as Texture).dispose();
                break;

            case "obj":
            case "fbx":
            case "gltf":
            case "stl":
                // 3D-oliot — varmista Object3D ja traverse
                if (resource instanceof Object3D && typeof resource.traverse === "function") {
                    resource.traverse((child) => {
                        if (child instanceof Mesh) {
                            child.geometry?.dispose();
                            if (Array.isArray(child.material)) {
                                child.material.forEach((mat) => mat.dispose());
                            } else {
                                child.material?.dispose();
                            }
                        }
                    });
                } else {
                    console.warn(`disposeResource: Expected Object3D for type=${type}`, resource);
                }
                break;

            default:
                console.warn(`disposeResource: Unsupported ResourceType=${type}`, resource);
        }
    }


    public disposeAll() {
        this.cache.forEach((item) => {
            this.disposeResource(item.resource, item.resourceType);
        });
        this.cache.clear();
    }
}
